import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { notification } from 'antd';
import { mapPropertiesToCascaderOptions } from 'utils/filter-builder';
import {
  RuleSeverity,
  useUpdateConsistencyRuleMutation,
  useCreateConsistencyConditionCheckMutation,
  useUpdateConsistencyConditionCheckMutation,
  useDeleteConsistencyConditionCheckMutation,
  GQLOperations,
} from 'types/dt-graphql-apollo-v2';
import { DigitalTwin } from 'types/graphql-apollo-v2';
import {
  ConditionOperator,
  ConsistencyRuleView,
  ConsistencySchemaFilterView,
  RuleOperator,
} from '../types';
import {
  useSchemaData,
  getAttributesForSchemaType,
  resolveSchemaContext,
} from '../useSchemaData';
import {
  type ConditionCheckState,
  Severity,
  Operator,
} from '../useNewConsistencyRuleDrawer';
import { LogicalRule, logicalRuleToFilter } from 'utils/rules';
import {
  emptyLogicalRule,
  countRelationalRules,
  hasIncompleteRelationalRule,
} from 'utils/nested-conditions';
import { safeParseFilter } from '../utils/safeParseFilter';

export { Severity, Operator };

const MAX_STEP = 7;

const REFETCH_CONFIG = {
  refetchQueries: [GQLOperations.Query.GetConsistencyCollections],
};

type UseEditConsistencyRuleDrawerParams = {
  rule: ConsistencyRuleView | undefined;
  visible: boolean;
  onClose: VoidFunction;
  schemaFilters: ConsistencySchemaFilterView[];
  digitalTwin: DigitalTwin;
};

export const useEditConsistencyRuleDrawer = ({
  rule,
  visible,
  onClose,
  schemaFilters,
  digitalTwin,
}: UseEditConsistencyRuleDrawerParams) => {
  const { schemas } = useSchemaData(digitalTwin);
  const [currentStep, setCurrentStep] = useState(0);
  const [creating, setCreating] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [customerDefinedIdInput, setCustomerDefinedIdInput] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');
  const [severityInput, setSeverityInput] = useState<RuleSeverity>(
    RuleSeverity.Critical
  );
  const [errorMessageInput, setErrorMessageInput] = useState('');
  const [labelListInput, setLabelListInput] = useState<string[]>([]);
  const [sourceSchemaFilterId, setSourceSchemaFilterId] = useState('');
  const [sourceAttribute, setSourceAttribute] = useState('');
  const [operator, setOperator] = useState<RuleOperator>(RuleOperator.IN_SET);
  const [targetSchemaFilterId, setTargetSchemaFilterId] = useState('');
  const [targetAttribute, setTargetAttribute] = useState('');
  const [conditionChecks, setConditionChecks] = useState<ConditionCheckState[]>(
    []
  );
  const [conditionChecksOnly, setConditionChecksOnly] = useState(false);
  const [originalConditionCheckIds, setOriginalConditionCheckIds] = useState<
    string[]
  >([]);
  const [sourceFiltersTree, setSourceFiltersTreeState] =
    useState<LogicalRule>(emptyLogicalRule);
  const [sourceFiltersDirty, setSourceFiltersDirty] = useState(false);
  const [targetFiltersTree, setTargetFiltersTreeState] =
    useState<LogicalRule>(emptyLogicalRule);
  const [targetFiltersDirty, setTargetFiltersDirty] = useState(false);

  const setSourceFiltersTree = (tree: LogicalRule) => {
    setSourceFiltersTreeState(tree);
    setSourceFiltersDirty(true);
  };
  const setTargetFiltersTree = (tree: LogicalRule) => {
    setTargetFiltersTreeState(tree);
    setTargetFiltersDirty(true);
  };
  const initializedRuleIdRef = useRef<string | undefined>(undefined);

  const [updateRule] = useUpdateConsistencyRuleMutation(REFETCH_CONFIG);
  const [createConditionCheck] =
    useCreateConsistencyConditionCheckMutation(REFETCH_CONFIG);
  const [updateConditionCheck] =
    useUpdateConsistencyConditionCheckMutation(REFETCH_CONFIG);
  const [deleteConditionCheck] =
    useDeleteConsistencyConditionCheckMutation(REFETCH_CONFIG);

  const sourceSchemaFilter = schemaFilters.find(
    (r) => r.externalId === sourceSchemaFilterId
  );
  const targetSchemaFilter = schemaFilters.find(
    (r) => r.externalId === targetSchemaFilterId
  );

  const sourceAttributes: string[] = sourceSchemaFilter
    ? getAttributesForSchemaType(sourceSchemaFilter.schemaType, schemas)
    : [];
  const targetAttributes: string[] = targetSchemaFilter
    ? getAttributesForSchemaType(targetSchemaFilter.schemaType, schemas)
    : [];

  const {
    schemaTypes: sourceSchemaTypes,
    selectedType: sourceSelectedType,
    typeProperties: sourceTypeProperties,
  } = resolveSchemaContext(sourceSchemaFilter, schemas);
  const {
    schemaTypes: targetSchemaTypes,
    selectedType: targetSelectedType,
    typeProperties: targetTypeProperties,
  } = resolveSchemaContext(targetSchemaFilter, schemas);

  const sourceFiltersCount = countRelationalRules(sourceFiltersTree);
  const targetFiltersCount = countRelationalRules(targetFiltersTree);

  const sourceConditionAttributeOptions = mapPropertiesToCascaderOptions(
    sourceSelectedType?.properties ?? [],
    sourceSchemaTypes
  );
  const targetConditionAttributeOptions = mapPropertiesToCascaderOptions(
    targetSelectedType?.properties ?? [],
    targetSchemaTypes
  );

  const hasInvalidName = !nameInput.trim();
  const hasInvalidErrorMessage = !errorMessageInput.trim();
  const hasInvalidSource = !sourceSchemaFilterId || !sourceAttribute;
  const hasInvalidTarget = !targetSchemaFilterId || !targetAttribute;
  const hasIncompleteSourceFilter =
    hasIncompleteRelationalRule(sourceFiltersTree);
  const hasIncompleteTargetFilter =
    hasIncompleteRelationalRule(targetFiltersTree);
  const hasInvalidValues =
    hasInvalidName ||
    hasInvalidErrorMessage ||
    hasInvalidSource ||
    hasInvalidTarget ||
    hasIncompleteSourceFilter ||
    hasIncompleteTargetFilter;

  useEffect(() => {
    if (!visible) {
      initializedRuleIdRef.current = undefined;
      return;
    }
    if (!rule || initializedRuleIdRef.current === rule.externalId) return;

    initializedRuleIdRef.current = rule.externalId;
    setNameInput(rule.name);
    setCustomerDefinedIdInput(rule.customerDefinedId ?? '');
    setDescriptionInput(rule.description ?? '');
    setSeverityInput(rule.severity);
    setErrorMessageInput(rule.errorMessage ?? '');
    setLabelListInput(rule.tags ?? []);
    setSourceSchemaFilterId(rule.sourceSchemaFilter.externalId);
    setSourceAttribute(rule.sourceAttribute);
    setOperator(rule.operator);
    setTargetSchemaFilterId(rule.targetSchemaFilter.externalId);
    setTargetAttribute(rule.targetAttribute);
    const loadedChecks = rule.conditionChecks.map((cc) => ({
      id: cc.externalId,
      sourceAttribute: cc.sourceAttribute,
      operator: cc.operator,
      targetAttribute: cc.targetAttribute,
      isLiteralValue: cc.isLiteralValue,
      useTargetSchema: cc.useTargetSchema,
    }));
    setConditionChecks(loadedChecks);
    setConditionChecksOnly(rule.conditionChecksOnly);
    setOriginalConditionCheckIds(loadedChecks.map((c) => c.id));
    setSourceFiltersTreeState(safeParseFilter(rule.sourceFilters));
    setSourceFiltersDirty(false);
    setTargetFiltersTreeState(safeParseFilter(rule.targetFilters));
    setTargetFiltersDirty(false);
    setCurrentStep(0);
  }, [visible, rule]);

  const nextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, MAX_STEP));
  };

  const onSaveRule = async () => {
    if (creating || hasInvalidValues || !rule) return;
    setCreating(true);

    try {
      await updateRule({
        variables: {
          id: rule.externalId,
          name: nameInput.trim(),
          customerDefinedId: customerDefinedIdInput.trim() || null,
          description: descriptionInput.trim() || null,
          sourceSchemaFilter: { _directRelation: sourceSchemaFilterId },
          sourceAttribute,
          sourceFilters: sourceFiltersDirty
            ? JSON.stringify(
                logicalRuleToFilter(sourceFiltersTree, sourceTypeProperties)
              )
            : (rule.sourceFilters ?? null),
          operator,
          targetSchemaFilter: { _directRelation: targetSchemaFilterId },
          targetAttribute,
          targetFilters: targetFiltersDirty
            ? JSON.stringify(
                logicalRuleToFilter(targetFiltersTree, targetTypeProperties)
              )
            : (rule.targetFilters ?? null),
          severity: severityInput,
          errorMessage: errorMessageInput,
          tags: labelListInput.length > 0 ? labelListInput : null,
          conditionChecksOnly,
        },
      });

      const currentIds = new Set(conditionChecks.map((c) => c.id));
      const removedIds = originalConditionCheckIds.filter(
        (id) => !currentIds.has(id)
      );
      const existingChecks = conditionChecks.filter((c) =>
        originalConditionCheckIds.includes(c.id)
      );
      const newChecks = conditionChecks.filter(
        (c) => !originalConditionCheckIds.includes(c.id)
      );

      await Promise.all([
        ...removedIds.map((id) => deleteConditionCheck({ variables: { id } })),
        ...existingChecks.map((c) =>
          updateConditionCheck({
            variables: {
              id: c.id,
              sourceAttribute: c.sourceAttribute,
              operator: c.operator,
              targetAttribute: c.targetAttribute,
              isLiteralValue: c.isLiteralValue,
              useTargetSchema: c.useTargetSchema,
            },
          })
        ),
      ]);

      // Creates run separately so we can track which succeeded.
      // If the save is retried after a partial failure, successfully-created
      // checks are treated as existing (updateConditionCheck) rather than new,
      // preventing a duplicate-key error from the backend.
      const createResults = await Promise.allSettled(
        newChecks.map((c) =>
          createConditionCheck({
            variables: {
              id: c.id,
              sourceAttribute: c.sourceAttribute,
              operator: c.operator,
              targetAttribute: c.targetAttribute,
              // FDM does not clear a boolean field when the value is absent on
              // create, so undefined is fine here. Updates send explicit false
              // (above) because FDM retains the previous value otherwise.
              isLiteralValue: c.isLiteralValue ? true : undefined,
              useTargetSchema: c.useTargetSchema ? true : undefined,
              ruleId: rule.externalId,
            },
          })
        )
      );

      const successfulCreateIds = newChecks
        .filter((_, i) => createResults[i].status === 'fulfilled')
        .map((c) => c.id);
      if (successfulCreateIds.length > 0) {
        setOriginalConditionCheckIds((prev) => [
          ...prev,
          ...successfulCreateIds,
        ]);
      }

      if (createResults.some((r) => r.status === 'rejected')) {
        throw new Error('Some condition checks could not be created');
      }

      notification.success({
        message: 'Comparison rule updated',
        description: `Successfully updated "${nameInput.trim()}"`,
      });

      onClose();
    } catch (err: unknown) {
      notification.error({
        message: 'Could not update comparison rule',
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setCreating(false);
    }
  };

  const onCancelHandler = () => {
    if (creating) return;
    setCurrentStep(0);
    onClose();
  };

  const handleSourceSchemaFilterChange = (id: string) => {
    setSourceSchemaFilterId(id);
    setSourceAttribute('');
    setSourceFiltersTreeState(emptyLogicalRule);
    setSourceFiltersDirty(true);
    if (id === targetSchemaFilterId) {
      setTargetSchemaFilterId('');
      setTargetAttribute('');
      setTargetFiltersTreeState(emptyLogicalRule);
      setTargetFiltersDirty(true);
    }
  };

  const handleTargetSchemaFilterChange = (id: string) => {
    setTargetSchemaFilterId(id);
    setTargetAttribute('');
    setTargetFiltersTreeState(emptyLogicalRule);
    setTargetFiltersDirty(true);
    if (id === sourceSchemaFilterId) {
      setSourceSchemaFilterId('');
      setSourceAttribute('');
      setSourceFiltersTreeState(emptyLogicalRule);
      setSourceFiltersDirty(true);
    }
  };

  const addConditionCheck = () => {
    setConditionChecks((prev) => [
      ...prev,
      {
        id: uuidv4(),
        sourceAttribute: '',
        operator: ConditionOperator.EQUALS,
        targetAttribute: '',
        isLiteralValue: false,
        useTargetSchema: false,
      },
    ]);
  };

  const removeConditionCheck = (id: string) => {
    setConditionChecks((prev) => {
      const next = prev.filter((c) => c.id !== id);
      if (next.length === 0) {
        setConditionChecksOnly(false);
      }
      return next;
    });
  };

  const updateConditionCheckLocal = <
    K extends keyof Omit<ConditionCheckState, 'id'>,
  >(
    id: string,
    field: K,
    value: ConditionCheckState[K]
  ) => {
    setConditionChecks((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c))
    );
  };

  const toggleConditionCheckIsLiteralValue = (id: string) => {
    setConditionChecks((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        const next = !c.isLiteralValue;
        return {
          ...c,
          isLiteralValue: next,
          useTargetSchema: next ? c.useTargetSchema : false,
        };
      })
    );
  };

  const toggleConditionCheckUseTargetSchema = (id: string) => {
    setConditionChecks((prev) =>
      prev.map((c) =>
        c.id === id
          ? { ...c, useTargetSchema: !c.useTargetSchema, sourceAttribute: '' }
          : c
      )
    );
  };

  return {
    currentStep,
    setCurrentStep,
    creating,
    nameInput,
    setNameInput,
    customerDefinedIdInput,
    setCustomerDefinedIdInput,
    descriptionInput,
    setDescriptionInput,
    severityInput,
    setSeverityInput,
    errorMessageInput,
    setErrorMessageInput,
    labelListInput,
    setLabelListInput,
    sourceSchemaFilterId,
    sourceAttribute,
    setSourceAttribute,
    operator,
    setOperator,
    targetSchemaFilterId,
    targetAttribute,
    setTargetAttribute,
    sourceSchemaFilter,
    targetSchemaFilter,
    sourceAttributes,
    targetAttributes,
    sourceSchemaTypes,
    sourceSelectedType,
    targetSchemaTypes,
    targetSelectedType,
    sourceConditionAttributeOptions,
    targetConditionAttributeOptions,
    sourceFiltersTree,
    setSourceFiltersTree,
    sourceFiltersCount,
    targetFiltersTree,
    setTargetFiltersTree,
    targetFiltersCount,
    hasInvalidName,
    hasInvalidErrorMessage,
    hasInvalidSource,
    hasInvalidTarget,
    hasIncompleteSourceFilter,
    hasIncompleteTargetFilter,
    hasInvalidValues,
    conditionChecks,
    conditionChecksOnly,
    setConditionChecksOnly,
    addConditionCheck,
    removeConditionCheck,
    updateConditionCheck: updateConditionCheckLocal,
    toggleConditionCheckIsLiteralValue,
    toggleConditionCheckUseTargetSchema,
    nextStep,
    onSaveRule,
    onCancelHandler,
    handleSourceSchemaFilterChange,
    handleTargetSchemaFilterChange,
  };
};
